import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, UserPlus, Loader2, User, Eye, EyeOff, CheckCircle, Sparkles, ShieldCheck, Truck, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerRegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    city: '',
    password: '',
    confirm_password: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const validate = () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      return 'Please enter your full name';
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      return 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      return 'Please enter your phone number';
    }
    if (formData.password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    if (formData.password !== formData.confirm_password) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await axios.post(`${API}/customers/register`, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        password: formData.password,
        city: formData.city.trim(),
      });

      setSuccess(true);
      // Redirect to login with pre-filled email after short delay
      setTimeout(() => {
        navigate(`/customer/login?email=${encodeURIComponent(formData.email.trim().toLowerCase())}`);
      }, 2000);
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <section className="relative pt-32 pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
          </div>

          <div className="max-w-md mx-auto px-4 relative z-10">
            <div
              className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-10 text-center"
              data-testid="customer-register-success"
            >
              <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-3">Welcome to HorseGO!</h1>
              <p className="text-gray-400 mb-2">
                Your account has been created successfully.
              </p>
              <p className="text-gray-400 text-sm">
                We sent your login details to <span className="text-amber-500">{formData.email}</span>.
              </p>
              <p className="text-gray-500 text-sm mt-6">Redirecting you to sign in...</p>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Navbar />

      <section className="relative flex-1 flex items-center overflow-hidden pt-24 pb-12 lg:py-16">
        {/* Background gradients (matches HeroSection) */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors mb-6 lg:mb-10"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.back') || 'Back'}</span>
          </button>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* LEFT — Marketing pitch (lg+ only) */}
            <div className="hidden lg:block">
              <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-8">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-amber-500 text-sm font-medium">For Customers</span>
              </div>

              <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
                Join HorseGO —<br />
                <span className="text-amber-500">your horses</span>,<br />
                in trusted hands.
              </h2>
              <p className="text-gray-400 text-lg mb-10 max-w-lg leading-relaxed">
                Create a free account to request quotes from verified transporters,
                compare them in seconds, and book your trip — all in one place.
              </p>

              <div className="space-y-5">
                {[
                  {
                    icon: Truck,
                    title: 'Verified transporters only',
                    desc: 'Every transporter is identity-verified before they can quote your trip.',
                  },
                  {
                    icon: ShieldCheck,
                    title: 'Secure CMI payments',
                    desc: 'Pay in MAD using cards from any Moroccan bank — safe and instant.',
                  },
                  {
                    icon: Zap,
                    title: 'Free to use, always',
                    desc: 'No subscription, no hidden fees. Pay only when you book a transport.',
                  },
                ].map((feature) => (
                  <div key={feature.title} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-11 h-11 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — Register card */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-6 sm:p-8 md:p-10 backdrop-blur-sm shadow-2xl">
                <div className="text-center mb-6 sm:mb-8">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <User className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Create your account</h1>
                  <p className="text-gray-400 text-sm sm:text-base">Join HorseGO to book trusted equine transport.</p>
                </div>

            {error && (
              <div
                className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6"
                data-testid="customer-register-error"
              >
                <p className="text-red-500 text-sm text-center">{error}</p>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="customer-register-form"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">First name *</Label>
                  <Input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder="John"
                    className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                    data-testid="register-first-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Last name *</Label>
                  <Input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                    data-testid="register-last-name-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Email *</Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                  data-testid="register-email-input"
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Phone number *</Label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+212 6XX XXX XXX"
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                  data-testid="register-phone-input"
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">City</Label>
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Casablanca"
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
                  data-testid="register-city-input"
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Password *</Label>
                <div className="relative">
                  <Input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="At least 6 characters"
                    className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl pr-12"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    data-testid="register-toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Confirm password *</Label>
                <div className="relative">
                  <Input
                    name="confirm_password"
                    type={showConfirm ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    placeholder="Re-enter your password"
                    className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl pr-12"
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    data-testid="register-toggle-confirm-btn"
                  >
                    {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-6 rounded-full text-lg"
                data-testid="register-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5 mr-2" />
                    Create account
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center leading-relaxed">
                By signing up, you agree to our{' '}
                <Link to="/privacy-policy" className="text-amber-500 hover:underline">
                  Privacy Policy
                </Link>{' '}
                and Terms of Service.
              </p>
            </form>

            <div className="mt-6 sm:mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-gray-400 mb-2 text-sm">Already have an account?</p>
              <Link
                to="/customer/login"
                className="text-amber-500 font-semibold hover:text-amber-400 transition-colors"
                data-testid="go-to-login-link"
              >
                Sign in →
              </Link>
            </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomerRegisterPage;
